import {Action} from 'com/gongxigames/math/core/game/enums/Action';
import {CommandLineInterface, consoleLogger, CoreDI, CurrencyFormatter, inject} from '@gongxigames/framework/core';
import {
    GlobalEventsMapForBackwardCompatibility,
    UIButtonsChangeEnableEvent, UIElementRepositionData,
    UIElementsChangeVisibilityEvent, UIElementsSwapLayerEvent
} from '@gongxigames/framework/slot/events';
import {PartialAutoplayOptions, UIButtons} from '@gongxigames/framework/slot/types';
import {KeypadControlsView} from '@gongxigames/framework/slot/modules/keypadControls/KeypadControlsView';
import {KeypadControlsViewMobile} from '@gongxigames/framework/slot/modules/keypadControls/KeypadControlsViewMobile';
import {KeypadControlsMainGameState} from '@gongxigames/framework/slot/modules/keypadControls/states/KeypadControlsMainGameState';
import {KeypadControlsFreeGamesState} from '@gongxigames/framework/slot/modules/keypadControls/states/KeypadControlsFreeGamesState';
import {KeypadControlsDuringAutoplayGameState} from '@gongxigames/framework/slot/modules/keypadControls/states/KeypadControlsDuringAutoplayGameState';
import {KeypadControlsDuringAutoplayFreeSpinsState} from '@gongxigames/framework/slot/modules/keypadControls/states/KeypadControlsDuringAutoplayFreeSpinsState';
import {ReelsEvents} from '@gongxigames/framework/slot/modules/reels';
import {AutoplayEvents} from '@gongxigames/framework/slot/modules/autoplay';
import {FlowModuleEvents} from '@gongxigames/framework/slot/modules/flow';
import {GenericPopupEvents, GenericPopupType} from '@gongxigames/framework/slot/modules/genericPopup';
import {BuyBonusPopUpEvents} from '@gongxigames/framework/slot/modules/buyBonus';
import {KeypadControlsViewEvents} from '@gongxigames/framework/slot/modules/keypadControls/KeypadControlsViewEvents';
import {KeypadControlsViewMobileLandscape} from '@gongxigames/framework/slot/modules/keypadControls/KeypadControlsViewMobileLandscape';
import {UIDisableReason} from '@gongxigames/framework/slot/modules/keypad';
import {KeypadControlsEvents} from '@gongxigames/framework/slot/modules/keypadControls/KeypadControlsEvents';
import {Module} from '@gongxigames/framework/slot/fragments/module/Module';
import {BigWinEvents} from '@gongxigames/framework/slot';
import {strictUiEmitter} from '@gongxigames/framework/slotV2/globals/strictUiEmitter';


// @ts-ignore
// TODO: refactor required
export class KeypadControlsModule extends Module<KeypadControlsView | KeypadControlsViewMobile, GlobalEventsMapForBackwardCompatibility> {
    public static readonly NAME: symbol = Symbol('KeypadControlsModule');

    public static readonly BASIC_STATE: string = 'basic';
    public static readonly FREESPINS_STATE: string = 'freespin';
    public static readonly AUTOPLAY_STATE: string = 'autoplay';
    public static readonly FREESPINS_AUTOPLAY_STATE: string = 'freespinautoplay';

    @inject(CoreDI.CurrencyFormatter)
    protected _currencyFormatter!: CurrencyFormatter;

    @inject(CoreDI.CLI)
    private _cli!: CommandLineInterface;

    protected _possibleBetValues: number[] = [];
    protected _lastBetIndex = -1;
    protected _currentBetIndex = 0;
    protected _currentBetValue = 0;

    protected _disabledButtonsByMap: any = {
        autoplay: [],
        info: [],
        bet: [],
        turbo: [],

        // on mobile
        home: [],
        menu: [],
        totalBetOpenButton: []
    };

    public init(): void {
        super.init();

        /* develblock:start */
        this._cli.addCommand('decreaseBet', () => {
            if (this._currentBetIndex === 0) {
                return;
            }

            this.onDecBetButtonClicked();
        });
        this._cli.addCommand('increaseBet', () => {
            if (this._currentBetIndex === this._possibleBetValues.length - 1) {
                return;
            }

            this.onIncBetButtonClicked();
        });
        /* develblock:end */
    }

    public updateBetButtons(): void {
        if (this._currentBetIndex === 0) {
            this.view.disableBetDecButton();
        }

        if (this._possibleBetValues && this._currentBetIndex === this._possibleBetValues.length - 1) {
            this.view.disableBetIncButton();
        }
    }

    protected setupViews(): void {
        if (this.isPlatformDesktop()) {
            this.setView(KeypadControlsView.NAME);
        } else {
            this.setView(KeypadControlsViewMobile.NAME);
        }

        if (this.settings.get('turboModeDisabled')) {
            this.view.disableForeverTurboCheckbox();
        }

        if (this.settings.get('autoSpinDisabled')) {
            this.view.disableForeverAutoplayButton();
        }

        this.view.updateTurboCheckbox(this.settings.get('quickSpin'));
    }

    protected setupStates(): void {
        this.addState(new KeypadControlsMainGameState(this, KeypadControlsModule.BASIC_STATE));
        this.addState(new KeypadControlsFreeGamesState(this, KeypadControlsModule.FREESPINS_STATE));
        this.addState(new KeypadControlsDuringAutoplayGameState(this, KeypadControlsModule.AUTOPLAY_STATE));
        this.addState(new KeypadControlsDuringAutoplayFreeSpinsState(this, KeypadControlsModule.FREESPINS_AUTOPLAY_STATE));

        this.gotoState(KeypadControlsModule.BASIC_STATE);
    }

    protected setupEvents(): void {
        super.setupEvents();

        this.onCurrentStateHandle(ReelsEvents.REELS_SPINNING_STARTED);
        this.onCurrentStateHandle(BigWinEvents.STARTED);
        this.onCurrentStateHandle(BigWinEvents.FINISHED);
        this.onCurrentStateHandle(AutoplayEvents.START_AUTOPLAY);
        this.onCurrentStateHandle(AutoplayEvents.STOP_AUTOPLAY);
        this.onCurrentStateHandle(KeypadControlsEvents.TOTAL_BET_CHANGED);
        this.onCurrentStateHandle(FlowModuleEvents.RESPIN_ON_FREESPINS_RESTORED);

        this.on('General.UI.ENABLE', this.enableAllListener);
        this.on('General.UI.DISABLE', this.disableAllListener);
        this.on('General.UI.Button.ENABLE', this.onEnableUIButton);
        this.on('General.UI.Button.DISABLE', this.onDisableUIButton);
        this.on('General.Game.INITIALIZED', this.onGameInitialized);
        this.on(KeypadControlsEvents.UPDATE_KEYPAD, this.updateKeypadValues);
        this.on(ReelsEvents.SPIN_ENDED, this.onAllReelsStopped);
        this.on('Autoplay.CHANGED_OPTION', this.onChangedAutoplayOption);

        this.on('General.UI.HideElement', this.hideElement);
        this.on('General.UI.ShowElement', this.showElement);
        this.on('General.UI.SWAP_LAYER', this.swapUIElementsLayer);
        this.on('General.UI.RepositionElements', this.repositionElements);
        strictUiEmitter.on('UI.bet.hide', this.hideBetPanel.bind(this));
        strictUiEmitter.on('UI.bet.show', this.showBetPanel.bind(this));
        this.on('General.Promotion.Campaign.Ended', this.onPromotionCampaingEnded);

        // for restore case
        this.on(FlowModuleEvents.FREESPINS_WIN, this.onFreeSpinsWin);
        this.on(FlowModuleEvents.FREESPINS_RESTORED, this.onFreeSpinsRestored);
        this.on(FlowModuleEvents.RESPIN_ON_FREESPINS_RESTORED, this.onFreeSpinsRestored);
        this.on(FlowModuleEvents.ENTER_BASIC_STATE, this.onEnterFlowBasicState);
        this.on(FlowModuleEvents.BET_RESTORE, this.onBetRestored);
        this.on(KeypadControlsEvents.TOGGLE_TURBO_BUTTON, this.onTurboCheckboxClicked);

        this.on(GenericPopupEvents.INTRO_STARTED, this.onPopupStarted);
        this.on(GenericPopupEvents.FADE_OUT_STARTED, this.onPopupFadeOutStarted);
        this.on(GenericPopupEvents.POPUP_CLOSED, this.onPopupClosed);

        this.on(BuyBonusPopUpEvents.BET_DEC_BUTTON_CLICKED, this.onDecBetButtonClicked);
        this.on(BuyBonusPopUpEvents.BET_INC_BUTTON_CLICKED, this.onIncBetButtonClicked);

        this.onView(KeypadControlsViewEvents.INFO_CLICKED, this.onInfoClicked);
        this.onView(KeypadControlsViewEvents.AUTOPLAY_CLICKED, this.onAutoplayClicked);
        this.onView(KeypadControlsViewEvents.TURBO_CHECKBOX_CLICKED, this.onTurboCheckboxClicked);
        this.onView(KeypadControlsViewEvents.BET_INC_CLICKED, this.onIncBetButtonClicked);
        this.onView(KeypadControlsViewEvents.BET_DEC_CLICKED, this.onDecBetButtonClicked);
        this.onView(KeypadControlsViewMobileLandscape.TOTAL_BET_WINDOW_CLICKED, this.toggleVisibleTotalBetWindow);
        this.onView(KeypadControlsViewMobileLandscape.HOME_CLICKED, this.onHomeClicked);
    }

    protected hideBetPanel(): void {
        this.view.hideBetPanel();
    }

    protected showBetPanel(): void {
        this.view.showBetPanel();
    }

    protected onPopupStarted(id: any, type: symbol): void {
        if (type === GenericPopupType.FREESPIN_INTRO) {
            this.onFreeSpinsIntroStarted();
        }

        this.currentState.handle(id, type);
    }

    protected onPopupClosed(id: any, type: symbol): void {
        if (type === GenericPopupType.FREESPIN_OUTRO) {
            this.onFreeSpinsOutroFinished();
        }

        this.currentState.handle(id, type);
    }

    protected onPopupFadeOutStarted(id: any, type: symbol): void {
        if (type === GenericPopupType.FREESPIN_INTRO) {
            this.onIntroFadingAnimationStarted();
        }

        if (type === GenericPopupType.FREESPIN_OUTRO) {
            this.onOutroFadingAnimationStarted();
        }

        this.currentState.handle(id, type);
    }

    public enableAll(reason: symbol): void {
        for (const key in this._disabledButtonsByMap) {
            if (this._disabledButtonsByMap.hasOwnProperty(key)) {
                this.enableByName(key, reason);
            }
        }
    }

    public enableByName(buttonName: string, reason: symbol): void {
        const disabledBy = this._disabledButtonsByMap[buttonName];
        const index = disabledBy.indexOf(reason);

        if (index !== -1) {
            disabledBy.splice(index, 1);

            if (!disabledBy.length) {
                this.enableButton(buttonName);
            }
        }
    }

    public disableAll(reason: symbol): void {
        for (const key in this._disabledButtonsByMap) {
            if (this._disabledButtonsByMap.hasOwnProperty(key)) {
                this.disableByName(key, reason);
            }
        }
    }

    public disableByName(buttonName: string, reason: symbol): void {
        const disabledBy = this._disabledButtonsByMap[buttonName];

        if (disabledBy.indexOf(reason) === -1) {
            disabledBy.push(reason);

            this.disableButton(buttonName);
        }
    }

    protected enableAllListener(eventID: symbol | string, reason: symbol): void {
        this.enableAll(reason);
    }

    protected disableAllListener(eventID: symbol | string, reason: symbol): void {
        this.disableAll(reason);
    }

    protected onEnableUIButton(eventID: string, event: UIButtonsChangeEnableEvent): void {
        this.enableByName(event.name, event.reason);
    }

    protected onDisableUIButton(eventID: string, event: UIButtonsChangeEnableEvent): void {
        this.disableByName(event.name, event.reason);
    }

    protected enableButton(name: string): void {
        switch (name) {
            case UIButtons.AUTOPLAY: {
                this.view.enableAutoplayButton();

                break;
            }

            case UIButtons.TURBO: {
                this.view.enableTurboCheckbox();
                this.view.updateTurboCheckbox(this.settings.get('quickSpin'));

                break;
            }

            case UIButtons.INFO: {
                this.view.enableInfoButton();

                break;
            }

            case UIButtons.BET: {
                this.view.enableBetButtons();
                this.updateBetButtons();

                break;
            }

            case UIButtons.MENU: {
                this.view.enableMenuButton();

                break;
            }

            case UIButtons.HOME: {
                this.view.enableHomeButton();

                break;
            }

            case UIButtons.TOTAL_BET_OPEN_BUTTON: {
                this.view.enableTotalBetOpenButton();

                break;
            }

            default: {
                consoleLogger.warn(`There's no action for button "${name}"`);
            }
        }
    }

    protected disableButton(name: string): void {
        switch (name) {
            case UIButtons.AUTOPLAY: {
                this.view.disableAutoplayButton();

                break;
            }

            case UIButtons.TURBO: {
                this.view.disableTurboCheckbox();

                break;
            }

            case UIButtons.INFO: {
                this.view.disableInfoButton();

                break;
            }

            case UIButtons.BET: {
                this.view.disableBetButtons();

                break;
            }

            case UIButtons.MENU: {
                this.view.disableMenuButton();

                break;
            }

            case UIButtons.HOME: {
                this.view.disableHomeButton();

                break;
            }

            case UIButtons.TOTAL_BET_OPEN_BUTTON: {
                this.view.disableTotalBetOpenButton();

                break;
            }

            default: {
                consoleLogger.warn(`There's no action for button "${name}"`);
            }
        }
    }

    protected onIntroFadingAnimationStarted(): void {
        this.view.playTransitionToFreeSpins();
    }

    protected onOutroFadingAnimationStarted(): void {
        this.view.playTransitionToMainGame();
    }

    protected onAllReelsStopped(): void {
        this.enableAll(UIDisableReason.SPINNING);
    }

    protected onFreeSpinsWin(): void {
        this.disableAll(UIDisableReason.FREE_SPIN_MODE);
    }

    protected onFreeSpinsRestored(id: any): void {
        this.disableAll(UIDisableReason.FREE_SPIN_MODE);
        this.view.playTransitionToFreeSpins();
        this.currentState.handle(id);
    }

    protected onEnterFlowBasicState(): void {
        if (this._possibleBetValues.indexOf(this._currentBetValue) === -1) {
            const defaultBet = this.logic.getDefaultBet();
            const index = this._possibleBetValues.indexOf(defaultBet);

            this._currentBetIndex = index === -1 ? 0 : index;
        }

        this.refreshBetPanel();
    }

    protected onBetRestored(): void {
        this._currentBetIndex = this.getBetLevelIndexFromValue(this.logic.getSessionBet());
        this.refreshBetPanel();
    }

    protected onFreeSpinsIntroStarted(): void {
        this.enableAll(UIDisableReason.TRIGGERING_FEATURE);
        this.disableAll(UIDisableReason.FREE_SPIN_MODE);
    }

    protected onFreeSpinsOutroFinished(): void {
        this.enableAll(UIDisableReason.TRIGGERING_FEATURE);
        this.enableAll(UIDisableReason.FREE_SPIN_MODE);
    }

    protected onBonusOutroFinished(): void {
        this.enableAll(UIDisableReason.TRIGGERING_FEATURE);
    }

    protected toggleVisibleTotalBetWindow(): void {
        const view = this.view as KeypadControlsViewMobile;

        if (view.isBetControlsOpened) {
            view.closeTotalBetWindow();
        } else {
            view.openTotalBetWindow();
        }
    }

    protected onHomeClicked(): void {
        this.eventManager.emit(KeypadControlsEvents.HOME_BUTTON_CLICKED);
    }

    protected onGameInitialized(): void {
        const initialBet = this.logic.nextAction !== Action.SPIN ? this.logic.getSessionBet() : this.logic.getDefaultBet();

        this.initialSetup(this.logic.getBets(), initialBet);
    }

    protected updateKeypadValues(): void {
        this.onGameInitialized();
    }

    protected initialSetup(possibleBetValues: number[], currentBetLevel: number): void {
        this._possibleBetValues = possibleBetValues;
        this._currentBetIndex = this.getBetLevelIndexFromValue(currentBetLevel);
        this._currentBetValue = currentBetLevel;
        this.refreshBetPanel();
        this.emitTotalBetChanged();
    }

    protected getBetLevelIndexFromValue(value: number): number {
        for (let i = 0; i < this._possibleBetValues.length; i++) {
            if (this._possibleBetValues[i] === value) {
                return i;
            }
        }

        return -1;
    }

    protected refreshBetPanel(): void {
        if (!this._possibleBetValues) {
            return;
        }

        const betValueText = this.getBetValueText();

        this.view.setBetValueText(betValueText);

        // if not disabled updateButtons()
        if (!(this._disabledButtonsByMap[UIButtons.BET] && this._disabledButtonsByMap[UIButtons.BET].length)) {
            this.view.enableBetButtons();
            this.updateBetButtons();
        }

        this.logic.betId = this._currentBetIndex;
    }

    protected getBetValueText(): string {
        const unformatedValue = this._currentBetIndex === -1
            ? this._currentBetValue * this.logic.getWager()
            : this._possibleBetValues[this._currentBetIndex] * this.logic.getWager();

        return this._currencyFormatter.formatCents(unformatedValue, true);
    }

    protected onPromotionCampaingEnded(): void {
        if (this._lastBetIndex < 0) {
            return;
        }

        this._currentBetIndex = this._lastBetIndex;
        this.refreshBetPanel();
        this.emitTotalBetChanged();
    }

    protected onDecBetButtonClicked(): void {
        this._currentBetIndex--;
        this._lastBetIndex = this._currentBetIndex;
        this.refreshBetPanel();
        this.emitTotalBetChanged();
        this.soundPlayer.play('betbuttondown');
    }

    protected emitTotalBetChanged(): void {
        this.logic.updateSessionBet();
        this.eventManager.emit(KeypadControlsEvents.TOTAL_BET_CHANGED, this._currentBetIndex, this.getBetValueText());
        this.eventManager.emit('General.UI.BET.CHANGED', this._logic.getBetInfo());
    }

    protected onIncBetButtonClicked(): void {
        this._currentBetIndex++;
        this._lastBetIndex = this._currentBetIndex;
        this.refreshBetPanel();
        this.emitTotalBetChanged();

        if (this._currentBetIndex === this._possibleBetValues.length - 1) {
            this.soundPlayer.play('betbuttonmax');
        } else {
            this.soundPlayer.play('betbuttonup');
        }
    }

    protected onInfoClicked(): void {
        this.eventManager.emit(KeypadControlsEvents.INFO_CLICKED);
    }

    protected onAutoplayClicked(): void {
        this.soundPlayer.play('buttonclick');
        this.eventManager.emit(KeypadControlsEvents.AUTOPLAY_CLICKED);
    }

    protected onTurboCheckboxClicked(eventID: string | symbol, isOn: boolean): void {
        this.settings.set('quickSpin', isOn);
        this.view.updateTurboCheckbox(isOn);
        this.eventManager.emit(KeypadControlsEvents.TURBO_BUTTON_CLICKED, isOn);
    }

    private onChangedAutoplayOption(_eventID: string, options: PartialAutoplayOptions): void {
        if (options.turboMode !== undefined) {
            const isOn = !!options.turboMode;

            this.view.updateTurboCheckbox(isOn);
            this.settings.set('quickSpin', isOn);
        }
    }

    private hideElement(_event: string, data: UIElementsChangeVisibilityEvent): void {
        switch (data.name) {
            case 'TotalBetOpen': {
                this.view.hideTotalBetOpenButton();
                break;
            }
            default:
                break;
        }
    }

    private showElement(_event: string, data: UIElementsChangeVisibilityEvent): void {
        switch (data.name) {
            case 'TotalBetOpen': {
                this.view.showTotalBetOpenButton();
                break;
            }
            default:
                break;
        }
    }

    private swapUIElementsLayer(_event: string, data: UIElementsSwapLayerEvent): void {
        this.view.swapUIElementsLayer(data);
    }

    private repositionElements(_event: string, data: UIElementRepositionData[]): void {
        this.view.repositionElements(data);
    }
}
